import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import PetOrbitLogo from '../components/PetOrbitLogo';

export default function Settings() {
  const sections = [
    {
      title: 'Account',
      items: [
        { icon: 'FiUser', label: 'Profile Information' },
        { icon: 'FiCreditCard', label: 'Subscription Plan', badge: 'Pro' },
      ]
    },
    {
      title: 'Preferences',
      items: [
        { icon: 'FiBell', label: 'Notifications', toggle: true, active: true },
        { icon: 'FiMoon', label: 'Dark Mode', toggle: true, active: false },
      ]
    },
    {
      title: 'Connected Devices',
      items: [
        { icon: 'FiWatch', label: 'Collar Tracker', value: 'Connected' },
        { icon: 'FiCamera', label: 'Smart Feeder', value: 'Offline' },
      ]
    },
    {
      title: 'Support & Help',
      items: [
        { icon: 'FiHelpCircle', label: 'Knowledge Base' },
        { icon: 'FiMessageCircle', label: 'Contact Support' },
        { icon: 'FiInfo', label: 'System Diagnostics' },
      ]
    }
  ];

  return (
    <div className="max-w-2xl mx-auto space-y-8 pb-12">
      <header className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-800 tracking-tight">System Settings</h1>
          <p className="text-slate-500 mt-1 font-medium">Configure orbital parameters and crew preferences.</p>
        </div>
        <PetOrbitLogo iconOnly className="w-12 h-12 opacity-40 grayscale" />
      </header>

      <div className="bg-white/80 backdrop-blur-xl rounded-[2.5rem] overflow-hidden shadow-[0_8px_32px_rgba(0,0,0,0.04)] border border-white">
        <div className="p-8 flex items-center gap-8 border-b border-slate-100 bg-gradient-to-r from-blue-50/30 to-transparent">
          <div className="w-24 h-24 rounded-[2rem] bg-gradient-to-br from-[#0047AB] to-[#FF7F50] p-1 shadow-xl shadow-blue-500/10">
            <div className="w-full h-full bg-white rounded-[1.8rem] flex items-center justify-center relative overflow-hidden">
              <div className="absolute inset-0 bg-[#F5EFE6] opacity-50" />
              <SafeIcon icon="FiUser" className="text-4xl text-slate-300 relative z-10" />
            </div>
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h2 className="text-2xl font-black text-slate-800 tracking-tight">Commander Jane</h2>
              <span className="px-2 py-0.5 bg-[#0047AB] text-white text-[8px] font-black rounded uppercase tracking-widest">Alpha</span>
            </div>
            <p className="text-slate-500 font-medium text-sm">jane.doe@orbitalpet.demo</p>
            <button className="mt-3 text-xs text-[#0047AB] font-black uppercase tracking-widest hover:underline border border-[#0047AB]/20 px-3 py-1.5 rounded-lg hover:bg-blue-50 transition-colors">Edit Profile</button>
          </div>
        </div>

        <div className="p-4">
          {sections.map((section) => (
            <div key={section.title} className="p-4">
              <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 mb-4 ml-2">
                {section.title}
              </h3>
              <div className="bg-white rounded-3xl border border-slate-100 overflow-hidden shadow-sm">
                {section.items.map((item, iIdx) => (
                  <div 
                    key={item.label}
                    className={`flex items-center justify-between p-5 hover:bg-slate-50 transition-all cursor-pointer ${
                      iIdx !== section.items.length - 1 ? 'border-b border-slate-100' : ''
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-2xl bg-[#F5EFE6] flex items-center justify-center text-[#0047AB] shadow-sm">
                        <SafeIcon icon={item.icon} className="text-lg" />
                      </div>
                      <span className="font-bold text-slate-700 tracking-tight">{item.label}</span>
                    </div>
                    <div className="flex items-center text-sm">
                      {item.badge && (
                        <span className="px-3 py-1 bg-gradient-to-r from-[#0047AB] to-[#4070F4] text-white text-[9px] font-black rounded-lg shadow-md shadow-blue-500/20 uppercase tracking-widest">
                          {item.badge}
                        </span>
                      )}
                      {item.value && (
                        <span className={`font-black text-[10px] uppercase tracking-widest ${item.value === 'Connected' ? 'text-green-500' : 'text-slate-400'}`}>
                          {item.value}
                        </span>
                      )}
                      {item.toggle && (
                        <div className={`w-12 h-6.5 rounded-full p-1 transition-all duration-300 ${item.active ? 'bg-[#FF7F50]' : 'bg-slate-200'}`}>
                          <motion.div 
                            className="w-4.5 h-4.5 bg-white rounded-full shadow-lg"
                            animate={{ x: item.active ? 22 : 0 }}
                          />
                        </div>
                      )}
                      {!item.toggle && !item.badge && !item.value && (
                        <SafeIcon icon="FiChevronRight" className="text-slate-300 text-xl" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <section className="bg-slate-100/50 rounded-[2.5rem] p-10 border border-slate-200/50 relative overflow-hidden group">
        <div className="absolute top-0 right-0 w-40 h-40 bg-blue-100 rounded-full blur-3xl opacity-50 -translate-y-1/2 translate-x-1/2 group-hover:scale-150 transition-transform duration-1000" />
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-6">
            <PetOrbitLogo iconOnly className="w-8 h-8" />
            <h3 className="text-xl font-black text-slate-800 tracking-tight">About PetOrbit</h3>
          </div>
          <p className="text-sm text-slate-600 leading-relaxed mb-8 font-medium max-w-lg">
            PetOrbit is a next-generation pet management ecosystem designed for modern families. 
            Our mission is to simplify the complexities of pet care through intelligent tracking, 
            shared timelines, and proactive health monitoring.
          </p>
          <div className="flex flex-wrap gap-6">
            <a href="#" className="text-[10px] font-black text-[#0047AB] uppercase tracking-[0.2em] hover:text-[#FF7F50] transition-colors">Terms of Service</a>
            <a href="#" className="text-[10px] font-black text-[#0047AB] uppercase tracking-[0.2em] hover:text-[#FF7F50] transition-colors">Privacy Policy</a>
            <a href="#" className="text-[10px] font-black text-[#0047AB] uppercase tracking-[0.2em] hover:text-[#FF7F50] transition-colors">Open Source</a>
          </div>
        </div>
      </section>

      <div className="text-center pt-8 pb-4 opacity-40">
        <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em]">PetOrbit System v2.5.0-Repair</p>
        <div className="mt-2 text-[9px] font-black text-slate-500 bg-white/50 backdrop-blur-sm inline-block px-4 py-2 rounded-full border border-slate-200 uppercase tracking-[0.1em]">
          Made by LingLink AI and DigLab AI
        </div>
      </div>
    </div>
  );
}